import React from "react";
import { BrokerSignatureMark, getBrokerSig } from "./broker_signature.jsx";
import { AGENT, IcArrow, IcBack, IcCheck, IcX, LISTING_AGENT, RECEIVING_BROKERAGE, TRANSFERRING_BROKERAGE, coListAgent, listingFormFor } from "./shared.jsx";
// Signature modal — DocuSign-style embedded
const { useState: s_uS, useEffect: s_uE, useRef: s_uR } = React;

function SignatureModal({ doc, previewOnly, signatureStyle, agent, onClose, onSigned }) {
  // signatureStyle: "docusign" | "minimal" | "wet"
  const [stage, setStage] = s_uS("review"); // review | signing | done
  const [sigName, setSigName] = s_uS(agent.name);
  const [sigDate] = s_uS(new Date().toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"}));
  const [initials, setInitials] = s_uS(agent.name.split(" ").map(w => w[0]).join(""));
  const [agreed, setAgreed] = s_uS(false);
  const [progress, setProgress] = s_uS(0);
  const canvasRef = s_uR(null);
  const [drawnSig, setDrawnSig] = s_uS(false);

  // pretend scrolling through pages
  s_uE(() => {
    if (stage === "review") {
      const t = setTimeout(() => setProgress(100), 600);
      return () => clearTimeout(t);
    }
  }, [stage]);

  function finishSign() {
    setStage("done");
    const when = new Date().toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"});
    setTimeout(() => onSigned && onSigned(when), 900);
  }

  // docusign-style: yellow tabs that lead you through
  const isDocu = signatureStyle === "docusign";
  const isWet  = signatureStyle === "wet";

  return (
    <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && onClose()}>
      <div className="modal-panel-mobile" style={{width: "min(1080px, 96vw)", maxHeight: "94vh", background: "var(--ep-bg-elevated)", borderRadius: 4, display: "flex", flexDirection: "column", animation: "slideUp 260ms var(--ep-ease)", overflow: "hidden"}}>

        {/* DocuSign-style header bar */}
        {isDocu && stage !== "done" && (
          <div style={{background: "#1a1a1a", color: "#fff", padding: "10px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 13}}>
            <div style={{display: "flex", alignItems: "center", gap: 14}}>
              <div style={{width: 24, height: 24, borderRadius: 4, background: "#ffcc00", color: "#1a1a1a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800}}>DS</div>
              <span style={{opacity: 0.8}}>Please review the document below.</span>
            </div>
            <button onClick={onClose} style={{background: "transparent", border: 0, color: "#fff", cursor: "pointer"}}><IcX/></button>
          </div>
        )}

        {/* Regular header for non-docu */}
        {(!isDocu || stage === "done") && (
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 28px", borderBottom: "1px solid var(--ep-border)", background: "var(--ep-origin)", color: "var(--ep-saguaro-blossom)"}}>
            <div>
              <div className="ep-caption" style={{color: "rgba(255,245,228,0.75)"}}>
                {previewOnly ? "Preview" : stage === "done" ? "Complete" : "Review & Sign"}
              </div>
              <div style={{font: "300 22px/1.1 var(--ep-font-display)", marginTop: 4, color: "var(--ep-saguaro-blossom)"}}>{doc.title}</div>
            </div>
            <button className="ep-btn ghost sm" onClick={onClose} style={{color: "var(--ep-saguaro-blossom)"}}><IcX/></button>
          </div>
        )}

        {/* Docusign yellow action strip */}
        {isDocu && stage === "review" && !previewOnly && (
          <div style={{background: "#ffefb0", borderBottom: "1px solid #e0c75a", padding: "10px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 13, color: "#3d3412"}}>
            <div className="hstack" style={{gap: 10}}>
              <div style={{background: "#ffcc00", padding: "4px 10px", borderRadius: 2, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em"}}>Start</div>
              <span>Scroll to review the document. Then click Start to begin signing.</span>
            </div>
            <button onClick={() => setStage("signing")}
              style={{background: "#ffcc00", color: "#1a1a1a", border: 0, padding: "8px 18px", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em", cursor: "pointer", borderRadius: 2}}>
              Start →
            </button>
          </div>
        )}

        {/* Body */}
        {stage !== "done" && (
          <div className="scroll-y" style={{flex: 1, background: isDocu ? "#e8e6df" : "#3d3412", padding: 0}}>
            <div className="pdf-shell" style={{background: isDocu ? "#e8e6df" : "#3d3412", maxHeight: "none"}}>
              <FakePDF doc={doc} agent={agent}/>
              {doc.pages > 1 && <FakePDF doc={doc} agent={agent} page={2}/>}
            </div>
          </div>
        )}

        {/* Footer actions — regular review */}
        {!isDocu && stage === "review" && !previewOnly && (
          <div style={{padding: "18px 28px", borderTop: "1px solid var(--ep-border)", background: "#FFFAF0"}}>
            <div className="progress-bar" style={{marginBottom: 14}}><span style={{width: `${progress}%`}}/></div>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16}}>
              <label style={{display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer"}}>
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{accentColor: "var(--ep-sunset)", width: 15, height: 15}}/>
                I've reviewed this document and agree to its terms.
              </label>
              <button className="ep-btn primary" onClick={() => setStage("signing")} disabled={!agreed}>
                Continue to Sign <IcArrow/>
              </button>
            </div>
          </div>
        )}

        {/* Preview-only footer */}
        {previewOnly && stage === "review" && (
          <div style={{padding: "18px 28px", borderTop: "1px solid var(--ep-border)", background: "#FFFAF0", display: "flex", justifyContent: "flex-end"}}>
            <button className="ep-btn secondary sm" onClick={onClose}>Close Preview</button>
          </div>
        )}

        {/* Signing interface */}
        {stage === "signing" && (
          <div style={{padding: 28, background: "#FFFAF0", borderTop: "1px solid var(--ep-border)"}}>
            <div className="ep-eyebrow" style={{color: "var(--ep-sunset)"}}>
              {isDocu ? "Sign Here" : isWet ? "Draw Your Signature" : "Adopt Your Signature"}
            </div>
            <h3 style={{font: "300 26px/1.15 var(--ep-font-display)", margin: "8px 0 20px"}}>
              {isWet ? "Draw below" : "Confirm your full name as it should appear."}
            </h3>

            <div style={{display: "grid", gridTemplateColumns: "2fr 1fr", gap: 20, marginBottom: 20}}>
              {isWet ? (
                <div>
                  <div className="ep-eyebrow">Signature</div>
                  <SigPad canvasRef={canvasRef} onDrawn={() => setDrawnSig(true)}/>
                  <button className="ep-link" style={{fontSize: 12, marginTop: 6, background: "transparent", border: 0, cursor: "pointer"}}
                    onClick={() => { const c = canvasRef.current; if (c) c.getContext("2d").clearRect(0,0,c.width,c.height); setDrawnSig(false); }}>
                    Clear
                  </button>
                </div>
              ) : (
                <label className="ep-field">
                  <span className="ep-eyebrow">Full Name</span>
                  <input className="ep-input" value={sigName} onChange={e => setSigName(e.target.value)}/>
                  <div className="sig-pad typed" style={{marginTop: 10}}>{sigName}</div>
                </label>
              )}
              <label className="ep-field">
                <span className="ep-eyebrow">Initials</span>
                <input className="ep-input" value={initials} onChange={e => setInitials(e.target.value.slice(0,4).toUpperCase())}/>
                <div className="sig-pad typed" style={{marginTop: 10, textAlign: "center", fontSize: 36}}>{initials}</div>
              </label>
            </div>

            <div style={{fontSize: 12, color: "var(--ep-fg-muted)", marginBottom: 20, maxWidth: "68ch", lineHeight: 1.5}}>
              By clicking <strong>Sign</strong>, I agree that the signature and initials above will be the electronic representation of my signature for all purposes on this document. Dated <strong>{sigDate}</strong>.
            </div>

            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
              <button className="ep-btn ghost" onClick={() => setStage("review")}><IcBack/> Back</button>
              <button className="ep-btn primary" onClick={finishSign}
                disabled={isWet ? !drawnSig : !sigName.trim()}>
                Sign {doc.title.split("—")[0].trim()} <IcCheck/>
              </button>
            </div>
          </div>
        )}

        {/* Done */}
        {stage === "done" && (
          <div style={{padding: 60, textAlign: "center"}}>
            <div style={{width: 72, height: 72, borderRadius: 999, background: "var(--ep-avocado)", color: "var(--ep-origin)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 20}}>
              <IcCheck size={36}/>
            </div>
            <h2 style={{font: "300 40px/1.1 var(--ep-font-display)", margin: "0 0 12px"}}>Signed.</h2>
            <p style={{color: "var(--ep-fg-muted)", maxWidth: "44ch", margin: "0 auto 24px"}}>
              A copy has been routed to your agent file and emailed to {agent.email}. Amber Valentine will counter-sign as Designated Broker.
            </p>
            <button className="ep-btn secondary sm" onClick={onClose}>Continue</button>
          </div>
        )}
      </div>
    </div>
  );
}

function SigPad({ canvasRef, onDrawn }) {
  s_uE(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#473C16"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineJoin = "round";
    let drawing = false;
    const getPos = (e) => {
      const r = c.getBoundingClientRect();
      const t = e.touches?.[0];
      return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
    };
    const down = (e) => { drawing = true; const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); onDrawn && onDrawn(); };
    const move = (e) => { if (!drawing) return; const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); e.preventDefault && e.preventDefault(); };
    const up = () => { drawing = false; };
    c.addEventListener("mousedown", down); c.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    c.addEventListener("touchstart", down); c.addEventListener("touchmove", move, {passive: false}); c.addEventListener("touchend", up);
    return () => {
      c.removeEventListener("mousedown", down); c.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
      c.removeEventListener("touchstart", down); c.removeEventListener("touchmove", move); c.removeEventListener("touchend", up);
    };
  }, []);
  return <canvas ref={canvasRef} width={420} height={120} className="sig-pad" style={{display: "block"}}/>;
}

// ----- EveryPlace Form EP·BBT — Buyer-Broker Agreement Transfer -----
function BBAFormPage({ m, agent, idx, total }) {
  const Fill = ({ value, ph }) => (
    <div style={{borderBottom: "1px solid #1a1a1a", paddingBottom: 3, minHeight: 18, fontFamily: "Consolas, monospace", fontSize: 12.5, color: value ? "#1a1a1a" : "#b9b3a3"}}>{value || ph || "\u00A0"}</div>
  );
  const Label = ({ children }) => (
    <div style={{fontSize: 8.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "#7a7363", fontWeight: 700, marginBottom: 5}}>{children}</div>
  );
  const Section = ({ children }) => (
    <div style={{fontSize: 9.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "#1a1a1a", fontWeight: 700, margin: "22px 0 14px", paddingBottom: 6, borderBottom: "1px solid #d8d2c2"}}>{children}</div>
  );
  return (
    <div className="pdf-page" style={{minHeight: 760}}>
      {/* Brand header */}
      <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4}}>
        <div>
          <div style={{fontFamily: "'PP Fragment Glare', serif", fontSize: 22, fontWeight: 500, letterSpacing: "-0.01em", color: "#1a1a1a"}}>EveryPlace</div>
          <div style={{fontSize: 8, letterSpacing: "0.2em", textTransform: "uppercase", color: "#9a9384", marginTop: 3}}>Arizona Residential Real Estate</div>
        </div>
        <div style={{textAlign: "right", fontSize: 8.5, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9a9384", lineHeight: 1.6}}>
          Form EP·BBT | Rev. 2026<br/>{total > 1 ? `Document ${idx} of ${total}` : "Page 1 of 1"}
        </div>
      </div>

      <h2 style={{fontSize: 19, margin: "18px 0 4px"}}>Authorization to Transfer Buyer-Broker Agreement</h2>
      <p style={{fontSize: 12, color: "#5a5447", margin: 0}}>Mutual agreement to transfer exclusive employment representation to a new brokerage.</p>

      <Section>Original Agreement Information</Section>
      <div style={{marginBottom: 14}}>
        <Label>Buyer / Tenant Name(s)</Label>
        <Fill value={m.buyer}/>
      </div>
      <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 28px", marginBottom: 14}}>
        <div><Label>Current Broker of Record (Firm)</Label><Fill value={m.currentBroker}/></div>
        <div><Label>Assigned Salesperson</Label><Fill value={m.salesperson}/></div>
      </div>
      <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 28px"}}>
        <div><Label>Commencement Date</Label><Fill value={m.commencement}/></div>
        <div><Label>Expiration Date</Label><Fill value={m.expiration}/></div>
      </div>

      <Section>Receiving Brokerage Information</Section>
      <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 28px", marginBottom: 14}}>
        <div><Label>Receiving Broker (New Firm)</Label><Fill value={m.receivingBroker}/></div>
        <div><Label>Receiving Salesperson</Label><Fill value={m.salesperson}/></div>
      </div>
      <div style={{width: "calc(50% - 14px)"}}>
        <Label>Effective Date of Transfer</Label><Fill value={m.effective}/>
      </div>

      <Section>Transfer &amp; Indemnification Agreement</Section>
      <p style={{fontSize: 11.5, lineHeight: 1.65}}>
        The undersigned Buyer(s) and Current Broker mutually agree to transfer the active Buyer-Broker Exclusive Employment Agreement referenced above to the Designated Receiving Broker. Effective immediately on the Transfer Date, the Current Broker of Record shall no longer represent the Buyer(s) and waives any rights to future commissions or compensation earned from transactions initiated after the effective date of this transfer.
      </p>
      <p style={{fontSize: 11.5, lineHeight: 1.65}}>
        Receiving Broker hereby assumes full professional responsibility and contractual obligations upon executing this agreement, succeeding to all rights and duties outlined in the original employment contract. In consideration of this transfer, the Receiving Broker agrees to indemnify, defend, and hold harmless the Current Broker of Record, its Agent(s), and Broker(s) from any and all claims, liabilities, damages, or demands that may arise out of this transaction or the subsequent representation of the Buyer(s).
      </p>
      <div style={{display: "flex", alignItems: "center", gap: 10, margin: "14px 0 4px", padding: "10px 14px", background: "#f4f1e7", border: "1px solid #d8d2c2", borderRadius: 2}}>
        <span style={{fontSize: 9.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "#7a7363", fontWeight: 700}}>Transfer Fee Consideration</span>
        <span style={{fontSize: 14, fontWeight: 700, color: "#1a1a1a", marginLeft: "auto"}}>$0.00 · No Fee</span>
      </div>

      <div style={{marginTop: 26, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "22px 28px", paddingTop: 16, borderTop: "1px solid #ccc"}}>
        <SigBlock label="Buyer Signature / Date"/>
        <SigBlock label="Buyer Signature / Date"/>
        <SigBlock label={`Current Broker — ${m.currentBroker}`}/>
        <SigBlock label={`Receiving Broker — ${m.receivingSigner}`}/>
      </div>

      <div style={{marginTop: 20, fontSize: 10, color: "#666", lineHeight: 1.5, borderTop: "1px solid #eee", paddingTop: 12}}>
        <strong>NOTE:</strong> This transfer agreement modifies the brokerage representation party only. All core search parameters, general property descriptions, and standard terms within the original contract remain intact under the administration of the Receiving Broker unless explicitly amended in writing by the Buyer and Receiving Broker.
      </div>
    </div>
  );
}

// Maps an association form's sig-order role label to a display label that names
// the fixed parties: George Laughton (Listing Agent), the onboarding agent
// (Co-List Agent), EveryPlace (New Listing Broker), My Home Group (Original).
function listingSigLabel(s, la, co, origBroker, newBroker) {
  if (/co-?\s*listing agent|co-?\s*list agent/i.test(s)) return `${s} — ${co.name}`;
  if (/listing agent/i.test(s)) return `Listing Agent — ${la.name}`;
  if (/new listing broker|\(to\)\s*listing broker/i.test(s)) return `${s} — ${newBroker}`;
  if (/original listing broker|\(from\)\s*listing broker/i.test(s)) return `${s} — ${origBroker}`;
  return s;
}

// Fake PDF content per doc type
function FakePDF({ doc, agent, page = 1 }) {
  // ----- Buyer-Broker Agreement Transfer batch (Form EP·BBT) -----
  if (doc.bbaList) {
    return (
      <>
        {doc.bbaList.map((m, i) => (
          <BBAFormPage key={i} m={m} agent={agent} idx={i + 1} total={doc.bbaList.length}/>
        ))}
      </>
    );
  }

  // ----- MHG Open Escrow Transfer Authorization -----
  if (doc.escrowMeta || doc.title.includes("Escrow Transfer")) {
    const m = doc.escrowMeta || {};
    const fee = m.fee ? Number(m.fee).toLocaleString("en-US", {minimumFractionDigits: 2}) : "____________";
    const Fill = ({ value, w = "auto" }) => (
      <span style={{display: "inline-block", minWidth: w, borderBottom: "1px solid #1a1a1a", padding: "0 6px", fontFamily: "Consolas, monospace", fontSize: 12, color: "#1a1a1a"}}>{value || "\u00A0"}</span>
    );
    return (
      <div className="pdf-page">
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, borderBottom: "2px solid #473C16", paddingBottom: 12}}>
          <div>
            <div style={{fontFamily: "Arial, sans-serif", fontSize: 17, fontWeight: 800, letterSpacing: "-0.01em", color: "#1a1a1a"}}>MY HOME GROUP</div>
            <div style={{fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", color: "#888", marginTop: 2}}>Real Estate</div>
          </div>
          <div style={{fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "#888", textAlign: "right"}}>Page {page} of {doc.pages || 1}</div>
        </div>

        <h2 style={{fontSize: 16}}>Open Escrow Authorization to Transfer to New Brokerage</h2>

        <div style={{margin: "18px 0", display: "grid", gridTemplateColumns: "auto 1fr", gap: "10px 12px", fontSize: 13, alignItems: "baseline"}}>
          <span style={{fontWeight: 700}}>Property Address:</span><Fill value={[m.address, m.city].filter(Boolean).join(", ")}/>
          <span style={{fontWeight: 700}}>Escrow Company:</span><Fill value={m.escrowCompany}/>
          <span style={{fontWeight: 700}}>Escrow Number:</span><Fill value={m.escrowNumber}/>
          <span style={{fontWeight: 700}}>Buyer:</span><Fill value={m.buyer}/>
          <span style={{fontWeight: 700}}>Seller:</span><Fill value={m.seller}/>
          <span style={{fontWeight: 700}}>MHG Agent:</span><Fill value={m.mhgAgent || agent.name}/>
        </div>

        <p style={{fontSize: 12.5, lineHeight: 1.7}}>
          The undersigned Buyer &amp; Seller mutually agree to transfer the above-mentioned file to{" "}
          <Fill value={m.receivingBroker} w="180px"/> <em>(Receiving Broker)</em>. The parties hereby
          acknowledge that effective immediately, {m.currentBroker || "My Home Group LLC"} (Current Broker of Record)
          shall no longer represent the Buyer/Seller in this transaction.
        </p>
        <p style={{fontSize: 12.5, lineHeight: 1.7}}>
          Receiving Broker hereby assumes full responsibility upon executing this agreement. In exchange for said
          transfer, Receiving Broker agrees to pay {m.currentBroker || "My Home Group LLC"} a fee equal to{" "}
          <strong>${fee}</strong> and to indemnify and defend Broker of Record and its Agent(s) and Broker(s)
          from any and all claims that may arise from this transaction.
        </p>

        <div style={{marginTop: 28, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "22px 28px", paddingTop: 18, borderTop: "1px solid #ccc"}}>
          <SigBlock label="Buyer"/>
          <SigBlock label="Buyer"/>
          <SigBlock label="Seller"/>
          <SigBlock label="Seller"/>
          <SigBlock label={`MHG Broker — ${m.currentBroker || "My Home Group LLC"}`}/>
          <SigBlock label={`Receiving Broker — ${m.receivingSigner || "Andrew Laughton"}`}/>
        </div>

        <div style={{marginTop: 22, fontSize: 10.5, color: "#666", lineHeight: 1.5, borderTop: "1px solid #eee", paddingTop: 12}}>
          <strong>NOTE:</strong> This form is for all escrows and will be signed by MHG once the parties (Buyer &amp;
          Seller) have agreed to transfer the escrow and the Receiving Broker has agreed to accept it.
          {m.newBuild && " For this new build, the Builder signs as the Seller, not as the party paying the commission."}
        </div>
        <div style={{marginTop: 14, fontSize: 10, color: "#888", textAlign: "center"}}>
          Executed copy routed to {agent.email} and andrew@laughtonteam.com.
        </div>
      </div>
    );
  }

  if (doc.k === "ep_ic" || doc.title.includes("Independent Contractor")) {
    return (
      <div className="pdf-page">
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, borderBottom: "2px solid #473C16", paddingBottom: 12}}>
          <div style={{fontFamily: "'PP Fragment Glare', serif", fontSize: 18, fontWeight: 500, letterSpacing: "-0.01em"}}>EveryPlace</div>
          <div style={{fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "#888"}}>Page {page} of {doc.pages}</div>
        </div>
        <h2>Independent Contractor Agreement</h2>
        <p style={{fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.1em"}}>Between EveryPlace ("Broker") and <strong style={{color: "#1a1a1a"}}>{agent.name}</strong> ("Licensee"), effective {new Date().toLocaleDateString()}.</p>
        <h3>1. Nature of Relationship</h3>
        <p>Licensee is retained as an independent contractor, not an employee. Licensee shall exercise professional judgment and discretion in performing real estate services and shall comply with the Arizona Revised Statutes governing licensed real estate activity.</p>
        <h3>2. Licensing</h3>
        <p>Licensee shall maintain an active Arizona real estate license (# {agent.license}) in good standing with the Arizona Department of Real Estate and shall promptly notify Broker of any disciplinary action or status change.</p>
        <h3>3. Supervision and Compliance</h3>
        <p>All real estate activity shall be conducted under the supervision of the Designated Broker, Amber Valentine. Licensee shall adhere to Broker's policies, the National Association of REALTORS® Code of Ethics, and all applicable fair housing laws.</p>
        <h3>4. Compensation</h3>
        <p>Licensee shall be compensated on a commission basis as described in Schedule A. No draw, salary, or guaranteed minimum is provided. Commission splits, referral fees, and bonuses are paid only after funds are received and cleared by the Broker's trust account.</p>
        <h3>5. Expenses</h3>
        <p>Licensee is responsible for personal business expenses including MLS dues, lockbox fees, licensing renewal, professional insurance (E&O), automobile expenses, and marketing expenses not provided by Broker.</p>
        <h3>6. Confidentiality</h3>
        <p>Licensee shall not disclose Broker's proprietary systems, client lists, CRM data, or transaction records. Followup Boss contacts assigned or generated during the term of this Agreement remain the property of Broker.</p>
        {page === 2 && (
          <>
            <h3>7. Termination</h3>
            <p>Either party may terminate this Agreement upon fifteen (15) days written notice. Pending transactions shall be handled per Schedule B. Listings shall be reassigned at Broker's discretion.</p>
            <h3>8. Signatures</h3>
            <div style={{marginTop: 32, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, paddingTop: 20, borderTop: "1px solid #ccc"}}>
              <SigBlock label={`Licensee — ${agent.name}`}/>
              <SigBlock label="Designated Broker — Amber Valentine"/>
            </div>
          </>
        )}
      </div>
    );
  }

  if (doc.title.includes("Listing Transfer") || doc.listingForm) {
    const f = doc.listingForm || (typeof listingFormFor === "function" ? listingFormFor() : {});
    const lm = doc.listingMeta || {};
    const property = lm.addr || doc.title.replace("Listing Transfer — ", "");
    const la = (typeof LISTING_AGENT !== "undefined") ? LISTING_AGENT : { name: "George Laughton", license: "" };
    const co = (typeof coListAgent === "function") ? coListAgent(agent) : { name: agent.name, license: agent.license };
    const origBroker = (typeof TRANSFERRING_BROKERAGE !== "undefined") ? TRANSFERRING_BROKERAGE : AGENT.currentBroker;
    const newBroker = (typeof RECEIVING_BROKERAGE !== "undefined") ? RECEIVING_BROKERAGE : "EveryPlace";
    const laText = la.license ? `${la.name} \u00B7 ADRE #${la.license}` : la.name;
    const coText = co.license ? `${co.name} \u00B7 ADRE #${co.license}` : co.name;
    const sigs = f.sigOrder || ["Seller", "Original Listing Broker", "New Listing Broker", "Listing Agent", "Co-Listing Agent"];
    return (
      <div className="pdf-page">
        {/* Association masthead */}
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, borderBottom: "2px solid #473C16", paddingBottom: 12}}>
          <div>
            <div style={{fontFamily: "Arial, sans-serif", fontSize: 15, fontWeight: 800, letterSpacing: "-0.01em", color: "#1a1a1a"}}>{f.name || "REALTOR\u00AE Association"}</div>
            <div style={{fontSize: 8.5, letterSpacing: "0.18em", textTransform: "uppercase", color: "#888", marginTop: 3}}>Official Member Form{f.short ? ` \u00B7 ${f.short}` : ""}</div>
          </div>
          <div style={{fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "#888", textAlign: "right"}}>Page {page} of {doc.pages || 1}</div>
        </div>

        <h2 style={{fontSize: 17, margin: "16px 0 4px"}}>{f.formTitle || "Authorization to Transfer Listing"}</h2>
        <p style={{fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.1em", margin: 0}}>Withdraw &amp; simultaneously re-list \u00B7 terms unchanged</p>

        <h3>Listing</h3>
        <div style={{display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 14px", fontSize: 12.5, alignItems: "baseline", marginBottom: 4}}>
          <span style={{fontWeight: 700}}>Current MLS #:</span><FillLine value={lm.mls}/>
          <span style={{fontWeight: 700}}>Property Address:</span><FillLine value={[property, lm.city].filter(Boolean).join(", ")}/>
          <span style={{fontWeight: 700}}>Seller(s):</span><FillLine value={lm.seller}/>
          <span style={{fontWeight: 700}}>Listing Agent:</span><FillLine value={laText}/>
          <span style={{fontWeight: 700}}>Co-List Agent:</span><FillLine value={coText}/>
        </div>

        <h3>Transfer</h3>
        <p style={{fontSize: 12, lineHeight: 1.65}}>
          The undersigned Broker(s) and Seller(s) mutually agree to withdraw the listing agreement on the above property from{" "}
          <strong>{origBroker}</strong> (Original Listing Broker) and simultaneously agree to re-list it with{" "}
          <strong>{newBroker}</strong> (New Listing Broker), listed by <strong>{la.name}</strong> as Listing Agent
          {co.name ? <> with <strong>{co.name}</strong> as Co-List Agent</> : null}. The list price,
          expiration date, and all other terms and conditions of the original listing agreement shall remain the same.
        </p>

        {f.note && (
          <div style={{margin: "12px 0", padding: "10px 14px", background: "#f4f1e7", border: "1px solid #d8d2c2", borderRadius: 2, fontSize: 11, lineHeight: 1.5, color: "#3d3412"}}>
            <strong style={{textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 9.5}}>Association note &mdash; </strong>{f.note}
          </div>
        )}

        <h3>Acknowledged By</h3>
        <div style={{marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 28px", paddingTop: 8}}>
          {sigs.map((s, i) => <SigBlock key={i} label={listingSigLabel(s, la, co, origBroker, newBroker)}/>)}
        </div>

        <div style={{marginTop: 20, fontSize: 10, color: "#666", lineHeight: 1.5, borderTop: "1px solid #eee", paddingTop: 12}}>
          <strong>SUBMIT TO:</strong> {f.submitTo || "Association membership services"}.
          {f.url && <> Official form on file: <span style={{color: "#9a6b3f"}}>{f.short} Listing Transfer PDF</span>.</>}
        </div>
      </div>
    );
  }

  // Generic (Laughton Team, Addenda)
  if (doc.assocMeta) {
    const m = doc.assocMeta;
    return (
      <div className="pdf-page">
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, borderBottom: "2px solid #473C16", paddingBottom: 12}}>
          <div style={{fontFamily: "'PP Fragment Glare', serif", fontSize: 17, fontWeight: 500}}>{m.name}</div>
          <div style={{fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "#888"}}>{m.url}</div>
        </div>
        <h2>Member Change of Broker Form</h2>
        <p style={{fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.1em"}}>To: {m.name} · Membership Services</p>

        <h3>Member Information</h3>
        <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 12, marginBottom: 16}}>
          <FormCell label="Full Name" value={agent.name}/>
          <FormCell label="ADRE License #" value={agent.license}/>
          <FormCell label="Email" value={agent.email}/>
          <FormCell label="Phone" value={agent.phone}/>
          {m.bucket === "mls" && <FormCell label="MLS Agent ID" value={agent.shortId || ""}/>}
          <FormCell label="Effective Date" value={new Date().toLocaleDateString()}/>
        </div>

        <h3>Broker Change</h3>
        <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 12, marginBottom: 16}}>
          <FormCell label="Prior Broker" value={AGENT.currentBroker}/>
          <FormCell label="New Broker" value="EveryPlace"/>
          <FormCell label="Designated Broker" value="Amber Valentine"/>
          <FormCell label="Broker License #" value="LC722428000"/>
        </div>

        <h3>Action Requested</h3>
        <p style={{fontSize: 12}}>☑ Transfer active membership to new brokerage of record.<br/>☐ New member application (if applicable).<br/>☐ Retain existing membership; update broker only.</p>

        <h3>Member Authorization</h3>
        <p style={{fontSize: 11}}>I authorize {m.name} to update my membership record to reflect the above brokerage change. I agree to the association's bylaws and code of ethics.</p>
        <div style={{marginTop: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, paddingTop: 12, borderTop: "1px solid #ccc"}}>
          <SigBlock label={`Member — ${agent.name}`}/>
          <SigBlock label="Designated Broker — J. Laughton"/>
        </div>
        <div style={{marginTop: 20, fontSize: 10, color: "#888", textAlign: "center"}}>
          This form will be emailed to {m.url} on completion. Copy to {agent.email}.
        </div>
      </div>
    );
  }

  // Generic (Laughton Team, Addenda)
  return (
    <div className="pdf-page">
      <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, borderBottom: "2px solid #473C16", paddingBottom: 12}}>
        <div style={{fontFamily: "'PP Fragment Glare', serif", fontSize: 18, fontWeight: 500}}>The Laughton Team</div>
        <div style={{fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "#888"}}>Page {page} of {doc.pages}</div>
      </div>
      <h2>{doc.title}</h2>
      <p style={{fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.1em"}}>Between the Laughton Team and <strong style={{color: "#1a1a1a"}}>{agent.name}</strong></p>
      <h3>1. Purpose</h3>
      <p>This agreement sets forth the terms under which Licensee operates as a member of the Laughton Team, an affiliated team within EveryPlace. All activity under this agreement is subject to the underlying Independent Contractor Agreement and to the oversight of the Designated Broker.</p>
      <h3>2. Team Resources</h3>
      <p>Team provides CRM (Followup Boss) access, shared operations staff, transaction coordination, marketing support, and access to the Laughton Team lead pool. Team branding standards apply to all marketing.</p>
      <h3>3. Splits & Fees</h3>
      <p>Standard commission splits, referral fees, and team contributions are set forth in Schedule A. Team-generated leads are governed by Addendum A. Licensee agrees to fair and consistent lead handling.</p>
      <h3>4. Obligations</h3>
      <p>Licensee shall respond to team-assigned leads within agreed SLAs, maintain FUB hygiene, attend team meetings, and complete required professional development. Client communications shall meet team standards for tone, clarity, and timeliness.</p>
      <h3>5. Termination & Transition</h3>
      <p>Either party may terminate this Agreement per the terms of the ICA. Team leads remain Team property; personal-sphere leads remain with Licensee, subject to the Confidentiality clause in the ICA.</p>
      {page === 2 && (
        <div style={{marginTop: 32, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, paddingTop: 20, borderTop: "1px solid #ccc"}}>
          <SigBlock label={`Licensee — ${agent.name}`}/>
          <SigBlock label="Team Lead — Amber Valentine"/>
        </div>
      )}
    </div>
  );
}

function SigBlock({ label }) {
  // If this block belongs to Amber Valentine (EveryPlace Designated Broker)
  // and she has adopted a signature, embed her stored mark automatically.
  const isAmber = typeof label === "string" && /Amber Valentine/i.test(label);
  const brokerSig = isAmber && typeof getBrokerSig === "function" ? getBrokerSig() : null;
  if (brokerSig) {
    return <BrokerSignatureMark sig={brokerSig} label={label} compact/>;
  }
  return (
    <div>
      <div style={{borderBottom: "1px solid #1a1a1a", height: 36}}/>
      <div style={{fontSize: 10, marginTop: 4, color: "#555"}}>{label}</div>
    </div>
  );
}

function FillLine({ value }) {
  return (
    <span style={{borderBottom: "1px solid #1a1a1a", paddingBottom: 2, fontFamily: "Consolas, monospace", fontSize: 12, color: value ? "#1a1a1a" : "#b9b3a3", minHeight: 16, display: "inline-block"}}>
      {value || "\u00A0"}
    </span>
  );
}

function FormCell({ label, value }) {
  return (
    <div>
      <div style={{fontSize: 9, color: "#888", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: 3}}>{label}</div>
      <div style={{padding: "6px 8px", background: "#fff", border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace"}}>{value}</div>
    </div>
  );
}

export { SignatureModal };
